import { useState, useCallback, useEffect } from 'react';

export interface EnrichmentActionParams {
  column?: string;
  columns?: string[];
  enrichmentType?: string;
}

export interface FindMoreActionParams {
  count?: number;
}

export interface ChatWorkflowsState {
  isChatPanelOpen: boolean;
  showMonitorSetup: boolean;
  showCampaignCreation: boolean;
  showEnrichmentFlow: boolean;
  showFindMoreFlow: boolean;
  enrichmentActionParams: EnrichmentActionParams | null;
  findMoreActionParams: FindMoreActionParams | null;
  enrichmentTriggeredFromChat: boolean;
  findMoreTriggeredFromChat: boolean;
}

export interface UseChatWorkflowsReturn extends ChatWorkflowsState {
  openChatPanel: () => void;
  closeChatPanel: () => void;
  setIsChatPanelOpen: (open: boolean) => void;
  closeAllWorkflows: () => void;
  startMonitorSetup: () => void;
  closeMonitorSetup: () => void;
  startCampaignCreation: () => void;
  closeCampaignCreation: () => void;
  startEnrichmentFlow: (params?: EnrichmentActionParams | null, triggeredFromChat?: boolean) => void;
  closeEnrichmentFlow: () => void;
  startFindMoreFlow: (params?: FindMoreActionParams | null, triggeredFromChat?: boolean) => void;
  closeFindMoreFlow: () => void;
  handleChatActionTrigger: (action: { type: string; payload?: any }) => boolean;
}

interface UseChatWorkflowsProps {
  tableId: string | undefined;
  initialChatMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    criteria?: string[];
  }>;
}

export const useChatWorkflows = ({ tableId, initialChatMessages }: UseChatWorkflowsProps): UseChatWorkflowsReturn => {
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(true);
  const [showMonitorSetup, setShowMonitorSetup] = useState(false);
  const [showCampaignCreation, setShowCampaignCreation] = useState(false);
  const [showEnrichmentFlow, setShowEnrichmentFlow] = useState(false);
  const [showFindMoreFlow, setShowFindMoreFlow] = useState(false);
  const [enrichmentActionParams, setEnrichmentActionParams] = useState<EnrichmentActionParams | null>(null);
  const [findMoreActionParams, setFindMoreActionParams] = useState<FindMoreActionParams | null>(null);
  const [enrichmentTriggeredFromChat, setEnrichmentTriggeredFromChat] = useState(false);
  const [findMoreTriggeredFromChat, setFindMoreTriggeredFromChat] = useState(false);

  // Auto-open chat panel when navigating with initial messages
  useEffect(() => {
    if (initialChatMessages && initialChatMessages.length > 0) {
      setIsChatPanelOpen(true);
    }
  }, [initialChatMessages]);

  // Reset state when tableId changes
  useEffect(() => {
    setShowCampaignCreation(false);
    setShowMonitorSetup(false);
    setShowEnrichmentFlow(false);
    setShowFindMoreFlow(false);
  }, [tableId]);

  const closeAllWorkflows = useCallback(() => {
    setShowCampaignCreation(false);
    setShowMonitorSetup(false);
    setShowEnrichmentFlow(false);
    setShowFindMoreFlow(false);
  }, []);

  const openChatPanel = useCallback(() => setIsChatPanelOpen(true), []);
  const closeChatPanel = useCallback(() => setIsChatPanelOpen(false), []);

  const startMonitorSetup = useCallback(() => {
    closeAllWorkflows();
    setShowMonitorSetup(true);
    setIsChatPanelOpen(true);
  }, [closeAllWorkflows]);

  const closeMonitorSetup = useCallback(() => setShowMonitorSetup(false), []);

  const startCampaignCreation = useCallback(() => {
    closeAllWorkflows();
    setShowCampaignCreation(true);
    setIsChatPanelOpen(true);
  }, [closeAllWorkflows]);

  const closeCampaignCreation = useCallback(() => setShowCampaignCreation(false), []);

  const startEnrichmentFlow = useCallback((params?: EnrichmentActionParams | null, triggeredFromChat: boolean = false) => {
    closeAllWorkflows();
    setEnrichmentActionParams(params || null);
    setEnrichmentTriggeredFromChat(triggeredFromChat);
    setShowEnrichmentFlow(true);
    setIsChatPanelOpen(true);
  }, [closeAllWorkflows]);

  const closeEnrichmentFlow = useCallback(() => setShowEnrichmentFlow(false), []);

  const startFindMoreFlow = useCallback((params?: FindMoreActionParams | null, triggeredFromChat: boolean = false) => {
    closeAllWorkflows();
    setFindMoreActionParams(params || null);
    setFindMoreTriggeredFromChat(triggeredFromChat);
    setShowFindMoreFlow(true);
    setIsChatPanelOpen(true);
  }, [closeAllWorkflows]);

  const closeFindMoreFlow = useCallback(() => setShowFindMoreFlow(false), []);

  // Handle chat action triggers from both UI and backend
  const handleChatActionTrigger = useCallback((action: { type: string; payload?: any }): boolean => {
    // Handle UI-triggered actions (from quick action buttons)
    if (action.type === 'startCampaignCreation') {
      startCampaignCreation();
      return true;
    }

    if (action.type === 'startMonitorSetup') {
      startMonitorSetup();
      return true;
    }

    if (action.type === 'startEnrichment') {
      startEnrichmentFlow(null, false);
      return true;
    }

    if (action.type === 'startFindMore') {
      startFindMoreFlow(null, false);
      return true;
    }

    // Handle backend API action types (from chat message actions)
    if (action.type === 'find_more_results') {
      const params = action.payload?.actionParameters;
      startFindMoreFlow(params ? { count: params.count } : null, true);
      return true;
    }

    if (action.type === 'enrich_column') {
      const params = action.payload?.actionParameters;
      startEnrichmentFlow(params ? {
        column: params.column,
        enrichmentType: params.enrichmentType
      } : null, true);
      return true;
    }

    if (action.type === 'send_emails' || action.type === 'show_email_modal') {
      startCampaignCreation();
      return true;
    }

    return false;
  }, [startCampaignCreation, startMonitorSetup, startEnrichmentFlow, startFindMoreFlow]);

  return {
    isChatPanelOpen,
    showMonitorSetup,
    showCampaignCreation,
    showEnrichmentFlow,
    showFindMoreFlow,
    enrichmentActionParams,
    findMoreActionParams,
    enrichmentTriggeredFromChat,
    findMoreTriggeredFromChat,
    openChatPanel,
    closeChatPanel,
    setIsChatPanelOpen,
    closeAllWorkflows,
    startMonitorSetup,
    closeMonitorSetup,
    startCampaignCreation,
    closeCampaignCreation,
    startEnrichmentFlow,
    closeEnrichmentFlow,
    startFindMoreFlow,
    closeFindMoreFlow,
    handleChatActionTrigger,
  };
};
